import { type ComponentPropsWithoutRef, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

export type NotificationsItemLayoutProps = Omit<ComponentPropsWithoutRef<'div'>, 'prefix' | 'title'> & {
  children?: ReactNode;
  /** Legacy rows clamp content to 2 lines; a caller with no fixed row height (e.g. links/buttons) opts out. */
  clampContent?: boolean;
  content?: ReactNode;
  createdAt?: string;
  isUI2: boolean;
  meta?: ReactNode;
  prefix?: ReactNode;
  prefixAddon?: ReactNode;
  suffix?: ReactNode;
  title?: ReactNode;
};

export const NotificationsItemLayout = memo(
  ({
    children,
    className,
    clampContent = true,
    content,
    createdAt,
    isUI2,
    meta,
    prefix,
    prefixAddon,
    suffix,
    title,
    ...restOfProps
  }: NotificationsItemLayoutProps) => {
    return (
      <div className={className} {...restOfProps}>
        <div
          className={cn(
            'flex min-w-0 grow items-start gap-4 border-gray-4 py-4',
            isUI2 ? 'border-b-0 py-3' : 'border-b',
          )}
        >
          <div
            className="relative flex shrink-0 items-center justify-end gap-2"
            data-testid="NOTIFICATIONS_ITEM_PREFIX"
          >
            <div className="relative inline-flex">
              {prefix}
              {prefixAddon}
            </div>
          </div>

          <div className={cn('flex min-w-0 grow items-start gap-4', isUI2 && 'flex-col gap-2')}>
            <div className="flex w-full min-w-0 grow flex-col gap-2">
              {!!title && (
                <div
                  className="w-full min-w-0 break-words text-14 text-gray-12 [&>b]:font-medium"
                  data-testid="NOTIFICATIONS_ITEM_TITLE"
                >
                  {title}
                </div>
              )}
              {isUI2 && !!meta && (
                <div className="w-full min-w-0 truncate text-12 text-gray-8" data-testid="NOTIFICATIONS_ITEM_META">
                  {meta}
                </div>
              )}
              {!!content && (
                <div className={cn('text-14 text-gray-9', !isUI2 && clampContent && 'line-clamp-2')}>{content}</div>
              )}
              {!isUI2 && (!!meta || !!createdAt) && (
                <div className="flex items-center gap-1 text-12 text-gray-8" data-testid="NOTIFICATIONS_ITEM_FOOTER">
                  {meta}
                  {meta && createdAt ? ` · ${createdAt}` : createdAt}
                </div>
              )}
            </div>

            {!!suffix && (
              <div className="flex shrink-0 items-start justify-end gap-2" data-testid="NOTIFICATIONS_ITEM_SUFFIX">
                {suffix}
              </div>
            )}

            {isUI2 && !!createdAt && (
              <div className="text-12 text-gray-8" data-testid="NOTIFICATIONS_ITEM_CREATED_AT">
                {createdAt}
              </div>
            )}
          </div>

          {children}
        </div>
      </div>
    );
  },
);

NotificationsItemLayout.displayName = 'NotificationsItemLayout';
